import { BaseReport, type ReportRunDeps, type SortAccessor } from './baseReport.js';
import type { ReportCache } from './reportCache.js';
import type { ApiClient } from './runtime.js';
import type { ReportDom, SegmentRow } from './types.js';
import { csvVal, downloadCsv, esc, isGroupLast } from './utils.js';

export type SuppressedSegmentMap = Map<string, Set<number>>;

export class SegmentReport extends BaseReport<SegmentRow> {
  private filterValue = '';
  private hideZeroCounts = true;
  private hideSuppressed = true;

  constructor(
    cache: ReportCache,
    apiClient: ApiClient,
    dom: ReportDom,
    deps: ReportRunDeps,
    private readonly suppressedSegmentMap: SuppressedSegmentMap,
  ) {
    super('segments', cache, apiClient, dom, deps);
  }

  override get reportLabel(): string {
    return 'Segment';
  }

  protected override buildCacheKey(slugs: string[]): string {
    return `segments|${[...slugs].sort().join(',')}`;
  }

  protected override fetchData(slugs: string[]): Promise<SegmentRow[]> {
    return this.apiClient.getSegments(slugs);
  }

  protected override resetFilterState(): void {
    this.filterValue = '';
  }

  protected override peopleAccessor(): (r: SegmentRow) => number {
    return (r) => r.segment_count ?? 0;
  }

  protected override summaryItemLabel(): string {
    return 'num_segments';
  }

  protected override filterRows(rows: SegmentRow[]): SegmentRow[] {
    return rows.filter((r) => {
      if (!(r.segment_name || '').toLowerCase().includes(this.filterValue)) return false;
      if (this.hideZeroCounts && (r.segment_count == null || r.segment_count === 0)) return false;
      if (this.hideSuppressed && this.isSuppressed(r)) return false;
      return true;
    });
  }

  private isSuppressed(row: SegmentRow): boolean {
    const ids = this.suppressedSegmentMap.get(row.client_slug);
    return ids ? ids.has(row.segment_id) : false;
  }

  protected override renderTitle(rows: SegmentRow[], filtered: SegmentRow[]): void {
    const totalPeople = filtered.reduce((sum, r) => sum + (r.segment_count ?? 0), 0);
    this.dom.title.innerHTML =
      `Segment Report  —  ${filtered.length} of ${rows.length} result${rows.length !== 1 ? 's' : ''} ` +
      `<span class="title-people">(${totalPeople.toLocaleString()} people)</span>` +
      this.reportTimestampHtml();
  }

  protected override renderToolbar(toolbar: HTMLElement): void {
    toolbar.innerHTML = `
      <input type="text" id="segment-filter" placeholder="Filter by segment name…" />
      <label class="toggle-label">
        <input type="checkbox" id="hide-zero-toggle" ${this.hideZeroCounts ? 'checked' : ''} />
        Hide zero counts
      </label>
      <label class="toggle-label">
        <input type="checkbox" id="hide-suppressed-toggle" ${this.hideSuppressed ? 'checked' : ''} />
        Hide suppressed segments
      </label>
    `;

    const filterEl = toolbar.querySelector<HTMLInputElement>('#segment-filter')!;
    filterEl.value = this.filterValue;
    filterEl.addEventListener('input', () => {
      this.filterValue = filterEl.value.toLowerCase();
      this.refreshView();
    });

    const zeroToggle = toolbar.querySelector<HTMLInputElement>('#hide-zero-toggle')!;
    zeroToggle.addEventListener('change', () => {
      this.hideZeroCounts = zeroToggle.checked;
      this.refreshView();
    });

    const suppressedToggle = toolbar.querySelector<HTMLInputElement>('#hide-suppressed-toggle')!;
    suppressedToggle.addEventListener('change', () => {
      this.hideSuppressed = suppressedToggle.checked;
      this.refreshView();
    });
  }

  protected override renderData(area: HTMLElement, rows: SegmentRow[], filtered: SegmentRow[]): void {
    this.appendClientSummary(area, filtered);

    if (filtered.length === 0) {
      const msg = document.createElement('div');
      msg.className = 'empty-msg';
      msg.textContent =
        rows.length === 0
          ? 'No segments found for the selected clients.'
          : 'No segments match your filter.';
      area.appendChild(msg);
      return;
    }

    const sorted = this.sortRows(filtered, segmentAccessor);

    const table = document.createElement('table');
    table.innerHTML = `
      <thead>
        <tr>
          ${this.thSortable('client_slug', 'Client')}
          ${this.thSortable('segment_name', 'Segment Name')}
          ${this.thSortable('segment_count', 'Count', 'right')}
        </tr>
      </thead>
      <tbody>
        ${sorted
        .map(
          (r, i) => `
          <tr${isGroupLast(sorted, i, 'client_slug') ? ' class="client-group-last"' : ''}>
            <td>${esc(r.client_slug)}</td>
            <td><a href="${esc(segmentUrl(r))}" target="_blank" rel="noopener">${esc(r.segment_name)}</a></td>
            <td class="num">${r.segment_count != null ? r.segment_count.toLocaleString() : '—'}</td>
          </tr>`,
        )
        .join('')}
      </tbody>`;

    area.appendChild(table);
    this.bindSortHeaders(table);
  }

  override exportCsv(): void {
    if (!this.lastData || this.lastData.length === 0) return;
    const filtered = this.filterRows(this.lastData);
    const visible = this.sortRows(filtered, segmentAccessor);

    let csv = 'Client,Segment Name,Count,Link\n';
    csv += visible
      .map(
        (r) =>
          `${csvVal(r.client_slug)},${csvVal(r.segment_name)},${r.segment_count ?? ''},${csvVal(segmentUrl(r))}`,
      )
      .join('\n');

    downloadCsv(csv, this.reportType);
  }
}

function segmentUrl(r: SegmentRow): string {
  return `https://fly.customer.io/workspaces/${encodeURIComponent(String(r.workspace_id))}/journeys/segments/${encodeURIComponent(String(r.segment_id))}/overview`;
}

const segmentAccessor: SortAccessor<SegmentRow> = (key) => {
  switch (key) {
    case 'client_slug':
      return (r) => r.client_slug;
    case 'segment_name':
      return (r) => r.segment_name;
    case 'segment_count':
      return (r) => r.segment_count ?? -1;
    default:
      return () => '';
  }
};
